import { Fragment, useRef, useState } from 'react'
import { api, errorMessage } from '../api/client'
import type { MoneyToVendor, VendorSettlement } from '../api/types'
import { BillActions, BillFooter, Letterhead } from '../components/bill'
import { shop } from '../lib/shop'
import {
  Amt,
  Card,
  ConfirmDelete,
  Dt,
  Empty,
  Field,
  Note,
  NumberCell,
  PageHead,
  Wt,
  Toast,
} from '../components/ui'
import { Kpi } from '../components/charts'
import { PersonPicker } from '../components/PersonPicker'
import { useApi } from '../hooks/useApi'
import { useVendorPicker } from '../hooks/usePickers'
import { billFilename, formatDate, formatMoney, monthStart, today } from '../lib/format'
import { whatsAppLink } from '../lib/phone'

/**
 * The grower bill: goods, less carriage, less advances, less commission.
 *
 * Commission on every line uses the percentage frozen on that delivery when it
 * arrived, not the percentage agreed with the grower today.
 *
 * Nothing on this page is coloured red. Owing a grower is ordinary trade, and
 * colouring it red every day would teach the eye to ignore red on the customer
 * side, where it means somebody has not paid.
 */

/**
 * The two ways money reaches a grower, in the order the bill reads them: what
 * he took before the flowers earned it, then what the shop has since paid him.
 *
 * Both are subtracted by the same arithmetic, so they are only ever presented
 * apart - a grower reading his bill is owed the difference between "you took
 * 2,000 ahead" and "you were paid 10,500".
 */
const MONEY_GIVEN = [
  { kind: 'ADVANCE', title: 'ایڈوانس', total: 'کل ایڈوانس' },
  { kind: 'PAYMENT', title: 'ادائیگی', total: 'کل ادائیگی' },
  { kind: 'RECOVERY', title: 'ایڈوانس کٹوتی', total: 'کل کٹوتی' },
] as const

/** The same two words again, this time to name a row in the correction list. */
/**
 * The spans a grower bill is actually asked for.
 *
 * <p>Worked out when tapped rather than once at module load, so a tab left
 * open past midnight does not quietly hand back yesterday's month.
 */
const SPANS = [
  ['یہ مہینہ', () => [monthStart(), today()]],
  [
    'پچھلا',
    () => {
      const now = new Date(today())
      const first = new Date(now.getFullYear(), now.getMonth() - 1, 1)
      const last = new Date(now.getFullYear(), now.getMonth(), 0)
      return [iso(first), iso(last)]
    },
  ],
  ['سب', () => ['2000-01-01', '2100-01-01']],
] as const satisfies readonly (readonly [string, () => readonly [string, string]])[]

/** A Date as the yyyy-mm-dd the date inputs and the API both speak. */
function iso(value: Date): string {
  const month = String(value.getMonth() + 1).padStart(2, '0')
  const day = String(value.getDate()).padStart(2, '0')
  return `${value.getFullYear()}-${month}-${day}`
}

const GIVEN_LABEL: Record<MoneyToVendor, string> = {
  ADVANCE: 'ایڈوانس',
  PAYMENT: 'ادائیگی',
  RECOVERY: 'ایڈوانس کٹوتی',
}

export function Settlement() {
  const vendors = useVendorPicker()
  const [vendorId, setVendorId] = useState<string | null>(null)
  const [from, setFrom] = useState(monthStart())
  const [to, setTo] = useState(today())
  const billRef = useRef<HTMLDivElement>(null)

  const settlement = useApi<VendorSettlement>(
    vendorId ? `/reports/vendor-settlement/${vendorId}?from=${from}&to=${to}` : null,
    [from, to],
  )

  const data = settlement.data
  /* His commission as it stands today - the per-delivery rates frozen on the
     bill may differ, which is the whole point of freezing them, so this is
     shown as context on the tile and never used in the arithmetic. */
  const vendor = vendors.rows.find((row) => row.vendorId === vendorId)
  const [editError, setEditError] = useState<string | null>(null)

  const [cutting, setCutting] = useState('')
  const [cutError, setCutError] = useState<string | null>(null)

  /*
   * Cutting an advance said nothing at all when it worked - only when it
   * failed. The figures above do change, but they are above, and this is the
   * one button on the page that moves lakhs. Pressing it twice because the
   * first press looked like nothing is how a grower loses a month.
   */
  const [cutDone, setCutDone] = useState<string | null>(null)
  const [cutBusy, setCutBusy] = useState(false)

  /*
   * What the grower had taken before this bill cut anything - what is still
   * standing, plus whatever this bill has already taken back. That is the
   * sentence the top of the bill needs: "you had twenty thousand", not "you
   * have fifteen thousand left", which only makes sense after reading further
   * down.
   */
  const standingAdvance = data
    ? Number(data.advanceOutstanding) + Number(data.recoveredTotal)
    : 0

  /**
   * Takes some of a standing advance back out of this bill.
   *
   * <p>Written down rather than worked out, because it is a decision and not a
   * consequence. An advance used to come off every bill the moment it existed,
   * which is not how the trade runs: you claw it back when trade is good and
   * leave it standing when it is not, so the grower keeps bringing his flowers
   * here. Recording it is also the only way next month's bill knows how much is
   * genuinely left - otherwise it would ask for the same money again.
   *
   * <p>Dated to the last day of the bill, so it lands on the bill being looked
   * at rather than on a later one.
   */
  async function cutAdvance() {
    if (!vendorId || !data) return
    setCutError(null)
    setCutDone(null)
    setCutBusy(true)
    try {
      await api.post(`/vendors/${vendorId}/advances`, {
        date: to,
        amount: cutting,
        note: 'بل میں سے کاٹا',
        kind: 'RECOVERY',
      })
      setCutDone(`${formatMoney(Number(cutting))} ایڈوانس کاٹ دیا`)
      setCutting('')
      settlement.reload()
      vendors.reload()
    } catch (err) {
      setCutError(errorMessage(err))
    } finally {
      setCutBusy(false)
    }
  }

  /**
   * Shows the refusal and then lets it through.
   *
   * <p>Every correction on this page goes through here. Catching the error and
   * stopping would leave the box on screen holding a figure the book rejected,
   * which reads exactly like a figure that saved; rethrowing lets the cell put
   * itself back to what the book actually says, so the red line and the box
   * agree with each other.
   */
  function refuse(err: unknown): never {
    setEditError(errorMessage(err))
    throw err
  }

  /** Every correction reloads the bill, so the figures above always match. */
  async function saveRate(supplyId: string, rate: string) {
    setEditError(null)
    try {
      await api.put(`/supplies/${supplyId}/rate`, { rate })
      settlement.reload()
    } catch (err) {
      refuse(err)
    }
  }

  async function saveKiraya(supplyId: string, kiraya: string) {
    setEditError(null)
    try {
      await api.put(`/supplies/${supplyId}/kiraya`, { kiraya })
      settlement.reload()
    } catch (err) {
      refuse(err)
    }
  }

  /**
   * Corrects a weight written down wrong when the lorry came in - 13.5 keyed as
   * 1.35, or a figure read off the wrong line of the slip.
   *
   * <p>Reloads the grower list as well as the bill, because the weight moves the
   * amount, which moves what the shop owes him, which is printed beside his name
   * in the picker at the top of this page.
   *
   * <p>The server refuses to shrink a delivery below what has already sold out of
   * it, and says why.
   */
  async function saveWeight(supplyId: string, weight: string) {
    setEditError(null)
    try {
      await api.put(`/supplies/${supplyId}/weight`, { weight })
      settlement.reload()
      vendors.reload()
    } catch (err) {
      refuse(err)
    }
  }

  /**
   * Removes a delivery written by mistake - the wrong grower, the wrong day, or
   * a weight typed twice.
   *
   * <p>The server refuses while anything has already sold out of that lot, and
   * says how many lines are in the way; those have to go first, or the sales
   * would be left pointing at flowers the book no longer says arrived. Like
   * every other deletion in this application it goes to the bin, not to nothing.
   */
  async function removeSupply(supplyId: string) {
    setEditError(null)
    try {
      await api.delete(`/supplies/${supplyId}`)
      settlement.reload()
      vendors.reload()
    } catch (err) {
      setEditError(errorMessage(err))
    }
  }

  /**
   * Corrects an advance or a payment without leaving this page.
   *
   * <p>The date and the note are sent back exactly as they came so that fixing a
   * figure cannot quietly wipe either of them - the server takes all three and
   * writes all three, and only the amount is on screen to be changed.
   */
  async function saveGiven(entry: VendorSettlement['advances'][number], amount: string) {
    setEditError(null)
    try {
      await api.put(`/vendors/advances/${entry.id}`, {
        date: entry.date,
        amount,
        note: entry.note ?? null,
      })
      settlement.reload()
      vendors.reload()
    } catch (err) {
      refuse(err)
    }
  }

  /** Goes to the same thirty-day bin as everything else, not to nothing. */
  async function removeGiven(advanceId: string) {
    setEditError(null)
    try {
      await api.delete(`/vendors/advances/${advanceId}`)
      settlement.reload()
      vendors.reload()
    } catch (err) {
      setEditError(errorMessage(err))
    }
  }

  const caption = data
    ? `السلام علیکم ${data.vendorName} صاحب\n${formatDate(data.from)} تا ${formatDate(data.to)} کا حساب\n` +
      `${data.vendorOwesShop ? 'ہمیں لینے ہیں' : 'آپ کو دینے ہیں'}: ${formatMoney(data.amountDue)} روپے\nشکریہ — ${shop.name}`
    : ''

  const waLink = data ? whatsAppLink(data.vendorPhone, caption) : null

  return (
    <>
      <div className="no-print">
        <PageHead title="زمیندار کھاتہ" hint="ایک عرصے کا پورا حساب اور بل۔" />

        <Card>
          <div className="row">
            <PersonPicker
              label="زمیندار"
              noun="زمیندار"
              vendor
              value={vendorId}
              onChange={setVendorId}
              people={vendors.people}
              recentIds={vendors.recentIds}
              loading={vendors.loading}
            />

            <Field label="اس تاریخ سے">
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </Field>

            <Field label="اس تاریخ تک">
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </Field>
          </div>

          {/* A grower's bill is nearly always one month, so the month should
              not cost two taps on a calendar. */}
          <div className="btn-row">
            {SPANS.map(([label, span]) => {
              const [a, b] = span()
              return (
                <button
                  key={label}
                  type="button"
                  className={`btn btn--small ${from === a && to === b ? '' : 'btn--ghost'}`}
                  onClick={() => {
                    setFrom(a)
                    setTo(b)
                  }}
                >
                  {label}
                </button>
              )
            })}
          </div>

          {/*
            The answer, before the bill rather than under it.

            <p>Thirty deliveries stand between choosing a grower and reading
            what he is owed, so the one figure the page exists to produce was
            the last thing on it. These four say it at the top and the bill
            below is where you go to see how it was arrived at.
          */}
          {data && (
            <div className="kpis ledger-kpis">
              <Kpi
                label="مال کی رقم"
                value={Number(data.goodsTotal)}
                context={`${data.rows.length} سپلائی`}
              />
              <Kpi
                label="آپ کا کمیشن"
                value={Number(data.commissionTotal)}
                context={vendor ? `${vendor.commissionPct}%` : undefined}
              />
              <Kpi
                label="ایڈوانس باقی"
                value={Number(data.advanceOutstanding)}
                /* «کٹا نہیں» against a nought reads as though something was
                   left uncut. A grower who has taken nothing ahead has nothing
                   to say about it. */
                context={
                  Number(data.recoveredTotal) > 0
                    ? `${formatMoney(data.recoveredTotal)} کاٹا`
                    : Number(data.advanceOutstanding) > 0
                      ? 'کٹا نہیں'
                      : undefined
                }
              />
              <Kpi
                hero
                label={data.vendorOwesShop ? 'زمیندار سے لینا ہے' : 'زمیندار کو دینا ہے'}
                value={Number(data.amountDue)}
                context={formatMoney(data.amountDue)}
              />
            </div>
          )}

          {/*
            Clawing back an advance, when the shop decides to.

            Sits on this page and not on the bill because it is a decision
            being made, not a figure being read. Empty by default and left
            empty most weeks: an advance that quietly came off every bill is
            exactly what this replaced.
          */}
          {data && standingAdvance > 0 && (
            <div className="cut-advance">
              <div className="cut-advance__says">
                <span>ایڈوانس گیا ہوا ہے</span>
                <b>{formatMoney(standingAdvance)}</b>
              </div>

              <Field label="اس بل میں سے کاٹیں (نہ کاٹنا ہو تو خالی چھوڑ دیں)">
                <input
                  inputMode="decimal"
                  placeholder="0"
                  value={cutting}
                  onChange={(e) => setCutting(e.target.value)}
                />
              </Field>

              <button
                type="button"
                className="btn btn--save"
                disabled={cutBusy || !(Number(cutting) > 0)}
                onClick={cutAdvance}
              >
                {cutBusy ? 'کٹ رہا ہے…' : 'ایڈوانس کاٹیں'}
              </button>
            </div>
          )}

          {cutError && <Note kind="error">{cutError}</Note>}
          <Toast message={cutDone} />

          {data && (
            <BillActions
              billRef={billRef}
              filename={billFilename(data.vendorName, from, to)}
              caption={caption}
              waLink={waLink}
              enabled
            />
          )}

          {data && !data.vendorPhone && (
            <p className="muted small">
              اِس زمیندار کا نمبر «زمیندار» والے صفحے پر ڈال دیں تو سیدھا اُن کی چیٹ کھولنے والا بٹن
              بھی آ جائے گا۔
            </p>
          )}
        </Card>

        {settlement.error && <Note kind="error">{settlement.error}</Note>}
        {!vendorId && <Empty>حساب دیکھنے کے لیے زمیندار چنیں۔</Empty>}

        {data && data.unpricedCount > 0 && (
          <Note kind="warn">
            اِس عرصے کی <span className="amt">{data.unpricedCount}</span> سپلائی کا ریٹ ابھی طے نہیں
            ہوا۔ وہ نیچے لسٹ میں تو ہیں لیکن رقم میں شامل نہیں — سب سے نیچے «ریٹ یا کرایہ بدلیں»
            والے خانے میں اُن کا ریٹ لکھ دیں۔
          </Note>
        )}
      </div>

      {data && (
        <div className="bill" ref={billRef}>
          <Letterhead vendor />

          <div className="bill__body">
            <div className="bill__meta">
              <div>
                <div className="bill__who">{data.vendorName}</div>
                <div className="muted small">
                  <Dt value={data.from} /> تا <Dt value={data.to} />
                </div>
              </div>
              {/*
                What he is holding, beside his name rather than inside the
                sum. If some of it came off this bill the second line says so,
                so the copy in his hand shows the before and the after without
                either figure pretending to be a deduction.
              */}
              {standingAdvance > 0 && (
                <div className="bill__standing">
                  <div className="muted small">ایڈوانس گیا ہوا</div>
                  <div className="bill__standing-amt">{formatMoney(standingAdvance)}</div>
                  {Number(data.recoveredTotal) > 0 && (
                    <div className="muted small">
                      اب باقی {formatMoney(data.advanceOutstanding)}
                    </div>
                  )}
                </div>
              )}

              <div className="bill__when">
                <div className="muted small">بل کی تاریخ</div>
                <div>
                  <Dt value={today()} />
                </div>
              </div>
            </div>

            <h3 className="bill__section-title is-vendor">سپلائی</h3>

            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>تاریخ</th>
                    <th>وزن</th>
                    <th>ریٹ</th>
                    <th>رقم</th>
                    <th>کرایہ</th>
                    <th>کمیشن</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row) => (
                    <tr key={row.supplyId}>
                      <td>
                        <Dt value={row.date} />
                      </td>
                      <td className="num">
                        <Wt value={row.weight} />
                      </td>
                      <td className="num">
                        {row.rate ? (
                          <Amt value={row.rate} />
                        ) : (
                          <span className="pill pill--wait">ریٹ باقی</span>
                        )}
                      </td>
                      <td className="num">{row.amount ? <Amt value={row.amount} /> : '—'}</td>
                      <td className="num">
                        <Amt value={row.kiraya} />
                      </td>
                      <td className="num">
                        {row.commission ? (
                          <>
                            <Amt value={row.commission} />{' '}
                            <small className="amt amt--muted">({row.commissionPct}%)</small>
                          </>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}

                  {data.rows.length === 0 && (
                    <tr>
                      <td colSpan={6}>
                        <Empty>اِن دنوں میں کوئی سپلائی نہیں آئی۔</Empty>
                      </td>
                    </tr>
                  )}

                  {data.rows.length > 0 && (
                    <tr className="is-total">
                      <td colSpan={3}>کل</td>
                      <td className="num">{formatMoney(data.goodsTotal)}</td>
                      <td className="num">{formatMoney(data.kirayaTotal)}</td>
                      <td className="num">{formatMoney(data.commissionTotal)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {MONEY_GIVEN.map((section) => {
              const given = data.advances.filter((advance) => advance.kind === section.kind)
              if (given.length === 0) {
                return null
              }
              const total =
                section.kind === 'PAYMENT'
                  ? data.paidTotal
                  : section.kind === 'RECOVERY'
                    ? data.recoveredTotal
                    : data.advanceTotal

              return (
                <Fragment key={section.kind}>
                  <h3 className="bill__section-title is-vendor">{section.title}</h3>
                  <div className="table-wrap">
                    <table>
                      <tbody>
                        {given.map((advance) => (
                          <tr key={advance.id}>
                            <td>
                              <Dt value={advance.date} />
                            </td>
                            <td>{advance.note ?? '—'}</td>
                            <td className="num">
                              <Amt value={advance.amount} />
                            </td>
                          </tr>
                        ))}
                        <tr className="is-total">
                          <td colSpan={2}>{section.total}</td>
                          <td className="num">{formatMoney(total)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Fragment>
              )
            })}

            <h3 className="bill__section-title is-vendor">حساب</h3>

            <div className="table-wrap">
              <table>
                <tbody>
                  {/*
                    Only what is actually being taken off today.

                    <p>A standing advance used to head this table and be
                    repeated at its foot. Among a column of «کاٹا» lines it
                    read as deducted when it was not, and the same figure
                    appearing twice in one sum is the kind of thing a grower
                    stops to argue about. It belongs with his name at the top
                    of the sheet - a fact about the man, not a line in the
                    day's arithmetic - and that is where it has gone.
                  */}
                  <tr>
                    <td>مال کی رقم</td>
                    <td className="num">{formatMoney(data.goodsTotal)}</td>
                  </tr>
                  <tr>
                    <td>کرایہ کاٹا</td>
                    <td className="num">{formatMoney(data.kirayaTotal)}</td>
                  </tr>
                  {Number(data.recoveredTotal) > 0 && (
                    <tr>
                      <td>ایڈوانس کاٹا</td>
                      <td className="num">{formatMoney(data.recoveredTotal)}</td>
                    </tr>
                  )}
                  <tr>
                    <td>ادائیگی کاٹی</td>
                    <td className="num">{formatMoney(data.paidTotal)}</td>
                  </tr>
                  <tr>
                    <td>کمیشن کاٹا</td>
                    <td className="num">{formatMoney(data.commissionTotal)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="final-line">
              <span className="final-line__label">
                {data.vendorOwesShop ? 'زمیندار سے لینا ہے' : 'زمیندار کو دینا ہے'}
              </span>
              <span className="final-line__value">{formatMoney(data.amountDue)}</span>
            </div>

            <BillFooter
              vendor
              note="مال کی رقم میں سے کرایہ، کمیشن اور ادائیگی کاٹ کر یہ حساب بنا ہے۔ ایڈوانس اُسی وقت کٹتا ہے جب کاٹا جائے۔"
            />
          </div>
        </div>
      )}

      {/*
        Outside the bill div on purpose: this never prints and never travels with
        the bill. The customer side has had this since the beginning - the figures
        are argued over standing at the counter, and the person holding the bill
        should be able to put a wrong one right there and then rather than walking
        to another screen and finding the row again.
      */}
      {data && (
        <div className="no-print">
          <Card
            title="ریٹ، کرایہ یا رقم بدلیں"
            actions={<span className="muted small">یہ لسٹ بل میں نہیں جاتی</span>}
          >
            <p className="muted small" style={{ marginTop: 0 }}>
              وزن یا ریٹ غلط لکھا گیا؟ کرایہ بدل گیا؟ ایڈوانس یا ادائیگی کی رقم ٹھیک کرنی ہے؟ سب
              یہیں بدل سکتے ہیں — اوپر والا بل خود بخود نیا بن جائے گا۔ پوری لائن غلط ہو تو «ڈیلیٹ»
              سے ہٹا دیں؛ وہ «مزید» والے بن میں تیس دن تک پڑی رہتی ہے اور واپس آ سکتی ہے۔ جس سپلائی
              میں سے مال بک چکا ہو، وہ نہ ہٹے گی اور نہ اُس کا وزن بکے ہوئے مال سے کم ہو سکے گا —
              پہلے وہ لائنیں ہٹانی پڑیں گی۔
            </p>

            {editError && <Note kind="error">{editError}</Note>}

            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>تاریخ</th>
                    <th>کیا</th>
                    <th>وزن</th>
                    <th>ریٹ</th>
                    <th>کرایہ</th>
                    <th>رقم</th>
                    <th className="col-actions"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row) => (
                    <tr key={row.supplyId}>
                      <td>
                        <Dt value={row.date} />
                      </td>
                      <td>سپلائی</td>
                      <td className="num">
                        {/* Three decimals: the market weighs in grams and 13.5 is
                            not 13.500 to a scale, nor to the amount it drives. */}
                        <NumberCell
                          ariaLabel="وزن"
                          value={row.weight}
                          width={90}
                          decimals
                          onCommit={(value) => saveWeight(row.supplyId, value)}
                        />
                      </td>
                      <td className="num">
                        <NumberCell
                          ariaLabel="ریٹ"
                          value={row.rate ?? ''}
                          width={100}
                          onCommit={(value) => saveRate(row.supplyId, value)}
                        />
                      </td>
                      <td className="num">
                        <NumberCell
                          ariaLabel="کرایہ"
                          value={row.kiraya}
                          width={90}
                          onCommit={(value) => saveKiraya(row.supplyId, value)}
                        />
                      </td>
                      <td className="num">
                        <strong>{row.amount ? <Amt value={row.amount} /> : '—'}</strong>
                      </td>
                      <td>
                        <ConfirmDelete onConfirm={() => void removeSupply(row.supplyId)} />
                      </td>
                    </tr>
                  ))}

                  {/*
                    Money given sits in the same list as the deliveries, because
                    the argument at the counter is about the bill as a whole -
                    the grower says the advance was 8,000 and not 10,000 while
                    the bill is on the screen, and making somebody walk to
                    another page and find the row again is how a correction
                    turns into a promise to fix it later.

                    There is nothing to type in the weight, rate and carriage
                    columns for these rows, so the note takes that space.
                  */}
                  {data.advances.map((given) => (
                    <tr key={given.id} className={given.kind === 'PAYMENT' ? 'row-payment' : undefined}>
                      <td>
                        <Dt value={given.date} />
                      </td>
                      <td>{GIVEN_LABEL[given.kind]}</td>
                      <td className="muted" colSpan={3}>
                        {given.note ?? '—'}
                      </td>
                      <td className="num">
                        <NumberCell
                          ariaLabel={`${GIVEN_LABEL[given.kind]} کی رقم`}
                          value={String(given.amount)}
                          width={110}
                          onCommit={(value) => saveGiven(given, value)}
                        />
                      </td>
                      <td>
                        <ConfirmDelete onConfirm={() => void removeGiven(given.id)} />
                      </td>
                    </tr>
                  ))}

                  {data.rows.length === 0 && data.advances.length === 0 && (
                    <tr>
                      <td colSpan={7}>
                        <Empty>اِن دنوں میں نہ کوئی سپلائی آئی، نہ کوئی رقم دی گئی۔</Empty>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <p className="muted small" style={{ marginTop: 10 }}>
              خانے میں لکھ کر <strong>Enter</strong> دبائیں یا باہر ٹچ کریں۔ سپلائی کی رقم خود بن
              جاتی ہے (وزن × ریٹ)، اور کمیشن اُسی فیصد پر لگتا ہے جو سپلائی آتے وقت طے تھا۔ ایڈوانس
              یا ادائیگی کو کسی دوسرے زمیندار کے نام نہیں کیا جا سکتا — ایسی صورت میں لائن ڈیلیٹ کر
              کے صحیح نام پر دوبارہ لکھیں۔
            </p>
          </Card>
        </div>
      )}
    </>
  )
}
